import { inject, Injectable, Type } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  CanActivateChildFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CommonRoute } from '../enums/common-route.enum';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { AuthorizationService } from '../services/authorization/authorization.service';
import { LogFactory } from '../services/logger/log-factory';
import { PlatformService } from '../services/platform/platform.service';
import { TokenStorageService } from '../services/token-storage/token-storage.service';

@Injectable({
  providedIn: 'root',
})
class AccessControlGuardService implements CanActivateChild {
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly platformService = inject(PlatformService);

  private readonly logger = inject(LogFactory).createLogger(
    AccessControlGuardService,
  );

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return this.authenticationService.hasValidSession().pipe(
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          this.logger.debug('User is not authenticated; redirecting to login');
          return of(this.createRedirectTree(CommonRoute.LOGIN, state.url));
        }

        // If authenticated, proceed to authorization checks.
        return this.checkUrlAccess(state.url).pipe(
          switchMap((urlAccessResult) => {
            if (urlAccessResult !== true) {
              return of(urlAccessResult); // No URL access, return UrlTree
            }
            return this.checkComponentAndPageAccess(route, state.url).pipe(
              switchMap((accessResult) => {
                if (accessResult !== true) {
                  return of(accessResult);
                }
                return this.mfaCheck(state);
              }),
            );
          }),
        );
      }),
    );
  }

  private mfaCheck(state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const accessToken = this.authenticationService.getAccessToken() || '';
    const decodedToken = this.tokenStorage.decodeToken(accessToken);

    if (decodedToken?.mfaSetupRequired && this.platformService.isAdminUi()) {
      this.logger.debug('MFA setup required; redirecting to MFA setup');
      if (state.url.includes(CommonRoute.ADMIN_MFA_SETUP)) {
        return of(true);
      }
      return of(
        this.createRedirectTree(CommonRoute.ADMIN_MFA_SETUP, state.url),
      );
    }

    if (decodedToken?.mfaRequired) {
      if (state.url.includes(CommonRoute.REQUEST_MFA_RESET)) {
        this.logger.debug('MFA required but requesting MFA reset; allowing access');
        return of(true);
      }
      this.logger.debug('MFA required; redirecting to login for verification');
      return of(this.createRedirectTree(CommonRoute.LOGIN, state.url));
    }

    return of(true);
  }

  private createRedirectTree(route: CommonRoute, returnUrl: string): UrlTree {
    return this.router.createUrlTree([route], {
      queryParams: { returnUrl },
    });
  }

  private normalizeUrlForAccess(url: string): string[] {
    // Strip query string / fragment — access is by path, not params (e.g. the
    // plan editor's ?tab=products deep-link must still resolve to /payment-plans/:id).
    url = url.split('?')[0].split('#')[0];
    // Generate candidates by normalizing dynamic segments (ids, uuids)
    const candidates = new Set<string>([url]);

    // Replace numeric ids with :id
    candidates.add(url.replace(/\/(\d+)(?=\/|$)/g, '/:id'));

    // Replace uuid-like ids with :id
    const uuidRegex =
      /\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12})(?=\/|$)/g;
    candidates.add(url.replace(uuidRegex, '/:id'));

    // Collapse multiple slashes
    return Array.from(candidates).map((u) =>
      u.replace(/\/+/, '/').replace(/\/+/, '/').replace(/\/+/, '/'),
    );
  }

  private checkUrlAccess(url: string): Observable<boolean | UrlTree> {
    const candidates = this.normalizeUrlForAccess(url);
    return from(
      Promise.all(
        candidates.map((u) => this.authorizationService.hasAccessToUrl(u)),
      ),
    ).pipe(
      map((results) => {
        const hasAnyAccess = results.some((r) => !!r);
        if (!hasAnyAccess) {
          this.logger.warn('Access denied to URL', { url });
          return this.createRedirectTree(CommonRoute.ACCESS_DENIED, url);
        }
        return true;
      }),
    );
  }

  private checkComponentAndPageAccess(
    route: ActivatedRouteSnapshot,
    returnUrl: string,
  ): Observable<boolean | UrlTree> {
    return this.resolveComponent(route).pipe(
      switchMap(async (resolvedComponent) => {
        if (!resolvedComponent) {
          this.logger.warn(
            'Could not resolve component for route (may be a layout route with only children)',
            { url: returnUrl },
          );
          return true;
        }

        const componentName = (resolvedComponent as any).name || '(unnamed)';

        // Check for component-specific access
        const componentAccessKey = (resolvedComponent as any)
          .componentAccessKey;
        if (componentAccessKey) {
          const hasAccess =
            await this.authorizationService.hasAccessToComponent(
              componentAccessKey,
            );
          if (!hasAccess) {
            this.logger.warn('Access denied to component', {
              componentName,
              componentAccessKey,
            });
            return this.createRedirectTree(
              CommonRoute.ACCESS_DENIED,
              returnUrl,
            );
          }
        }

        // Check for page-specific access
        const pageAccessKey = (resolvedComponent as any).pageAccessKey;
        if (pageAccessKey) {
          const hasAccess =
            await this.authorizationService.hasAccessToPage(pageAccessKey);
          if (!hasAccess) {
            this.logger.warn('Access denied to page', {
              componentName,
              pageAccessKey,
            });
            return this.createRedirectTree(
              CommonRoute.ACCESS_DENIED,
              returnUrl,
            );
          }
        }

        if (!componentAccessKey && !pageAccessKey) {
          this.logger.warn(
            'Component/page reachable via router has no @ComponentAccess or @PageAccess decorator',
            { componentName },
          );
        }

        return true; // All checks passed
      }),
    );
  }

  private resolveComponent(
    route: ActivatedRouteSnapshot,
  ): Observable<Type<any> | null> {
    if (route.component) {
      return of(route.component);
    }
    if (route.routeConfig?.loadComponent) {
      const loaded = route.routeConfig.loadComponent();
      // Check if it's a promise-like or an observable
      if (typeof (loaded as any).then === 'function') {
        return from(loaded as Promise<Type<any>>);
      }
      return from(loaded as Observable<Type<any>>);
    }
    return of(null);
  }
}

export const AccessControlGuard: CanActivateChildFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  return inject(AccessControlGuardService).canActivateChild(route, state);
};
